import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { formatDateTime, statusBadgeColor, whatsappLink, countryChip } from "../utils";
import { Phone, Mail, MessageSquare, X, Unlock, CalendarPlus, Pencil, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ScheduleAppointmentDialog } from "../pages/Calendar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const CLIENT_STATUSES = ["Not Contacted", "Contacted", "Interested", "Qualified", "Meeting Set", "Won", "Lost", "Unreachable"];

export default function DetailPanel({ open, onClose, entityType, entity, onChanged }) {
  // entityType: "candidate" | "lead" | "client"
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(false);
  // local "editing" mode for client fields (admins only)
  const isAdmin = user?.role === "admin";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);
  // Local copy of the entity so inline edits reflect immediately without
  // waiting for the parent list refresh to come back.
  const [local, setLocal] = useState(entity);
  useEffect(() => { setLocal(entity); }, [entity]);
  const collection = entityType === "candidate" ? "candidates"
    : entityType === "lead" ? "leads" : "clients";

  useEffect(() => {
    if (!open || !entity?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/${collection}/${entity.id}/notes`);
        setNotes(data);
      } catch { /* ignore */ }
    })();
  }, [open, entity?.id, collection]);

  // Load staff list once when the client detail panel opens for admin (for the
  // "Assigned to" dropdown in edit mode). Non-admins can still edit other
  // fields, just not reassignment.
  useEffect(() => {
    if (!open || entityType !== "client" || !isAdmin || staffList.length > 0) return;
    api.get("/staff").then(({ data }) =>
      setStaffList((data || []).filter((u) => u.is_active !== false))
    ).catch(() => {});
  }, [open, entityType, isAdmin, staffList.length]);

  const startEdit = () => {
    const e = local || {};
    setForm({
      company_name: e.company_name || "",
      industry:     e.industry || "",
      state:        e.state || "",
      city:         e.city || "",
      phone:        e.phone || "",
      email:        e.email || "",
      website:      e.website || "",
      contact_person: e.contact_person || "",
      status:       e.status || "Not Contacted",
      assigned_to:  e.assigned_to || "",
    });
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setForm({}); };
  const saveEdit = async () => {
    if (!local?.id) return;
    setSaving(true);
    try {
      const body = { ...form };
      // Non-admins cannot reassign — strip the field from the body.
      if (!isAdmin) delete body.assigned_to;
      // Empty string for assigned_to == unassign (null)
      if (body.assigned_to === "") body.assigned_to = null;
      if (body.assigned_to === "__unassigned") body.assigned_to = null;
      const { data } = await api.put(`/clients/${local.id}`, body);
      setLocal(data);
      setEditing(false);
      toast.success("Client updated");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const doDelete = async () => {
    if (!local?.id) return;
    setDeleting(true);
    try {
      await api.delete(`/${collection}/${local.id}`);
      toast.success(`${entityType === "client" ? "Client" : entityType === "lead" ? "Lead" : "Candidate"} deleted`);
      setDeleteOpen(false);
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setDeleting(false);
    }
  };

  if (!local) return null;

  const addNote = async () => {
    if (!newNote.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/${collection}/${local.id}/notes`, { content: newNote });
      setNotes([data, ...notes]);
      setNewNote("");
      onChanged?.();
      toast.success("Note added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally { setLoading(false); }
  };

  const claim = async () => {
    try {
      await api.post(`/candidates/${local.id}/claim`);
      toast.success("Candidate claimed");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const release = async () => {
    try {
      await api.post(`/candidates/${local.id}/release`);
      toast.success("Claim released");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const title = local.name || local.company_name || "Details";
  const phone = local.phone || "";
  const email = local.email || "";
  const sender = user?.display_name || "";
  const waMsg = entityType === "client"
    ? (() => {
        const company = local.company_name || "there";
        const contact = (local.contact_person || "").split(/\s+/)[0];
        const hi = contact ? `Hi ${contact}` : "Hi";
        return `${hi}, this is ${sender} from Mercy Consultancy.\n\n` +
          `We are a licensed labour recruitment agency specialising in foreign worker supply, ` +
          `permit renewals (VP(TE), PLKS), FOMEMA and Employment Pass processing for Malaysian businesses.\n\n` +
          `I was going through our list of ${company} and wanted to reach out — ` +
          `are you currently looking for workers, or do you have any permits coming up for renewal in the next few months?\n\n` +
          `Happy to send over our service list and rates if helpful.\n\nThanks!`;
      })()
    : entityType === "lead"
    ? `Hi ${local.name || ""}, this is ${sender} from Mercy Consultancy. ` +
      `Following up on your enquiry — happy to answer any questions and share our service details whenever convenient.`
    : `Hello ${local.name || ""}, this is ${sender} from Mercy Consultancy. ` +
      `I would like to follow up regarding your job application` +
      (local.position ? ` for ${local.position}` : "") +
      `.\n\nAre you still interested? Please let me know a good time to call. Thank you.`;
  const wa = whatsappLink(phone, waMsg);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-[#0f1626] border-slate-800 text-slate-100 overflow-y-auto"
        data-testid="detail-panel"
      >
        <SheetHeader className="space-y-2">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs text-slate-400 uppercase tracking-wider num">{entityType}</div>
              <SheetTitle className="text-xl text-slate-100">{title}</SheetTitle>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}
              className="text-slate-400 hover:text-white" data-testid="detail-close">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {entityType === "candidate" && (
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Badge className="bg-blue-500/15 text-blue-300 border border-blue-500/30 num">{local.category}</Badge>
              <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border hairline surface-2 num">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusBadgeColor(local.status) }} />
                {local.status}
              </span>
              <span className="text-xs text-slate-400 num">{countryChip(local.country)}</span>
              {local.permit === "yes" && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Permit</Badge>}
            </div>
          )}
        </SheetHeader>

        {/* Quick actions */}
        <div className="mt-5 grid grid-cols-3 gap-2">
          <a href={phone ? `tel:${phone}` : "#"} className={!phone ? "pointer-events-none opacity-40" : ""}>
            <Button variant="outline" className="w-full border-slate-700 hover:border-blue-500" data-testid="detail-call">
              <Phone className="w-4 h-4 mr-1.5" /> Call
            </Button>
          </a>
          <a href={email ? `mailto:${email}` : "#"} className={!email ? "pointer-events-none opacity-40" : ""}>
            <Button variant="outline" className="w-full border-slate-700 hover:border-blue-500" data-testid="detail-email">
              <Mail className="w-4 h-4 mr-1.5" /> Email
            </Button>
          </a>
          <a href={wa || "#"} target="_blank" rel="noreferrer" className={!wa ? "pointer-events-none opacity-40" : ""}>
            <Button variant="outline" className="w-full border-slate-700 hover:border-emerald-500" data-testid="detail-whatsapp">
              <MessageSquare className="w-4 h-4 mr-1.5" /> WhatsApp
            </Button>
          </a>
        </div>

        {entityType === "client" && (
          <Button onClick={() => setScheduleOpen(true)}
            className="mt-2 w-full bg-emerald-600 hover:bg-emerald-500"
            data-testid="detail-schedule-appointment">
            <CalendarPlus className="w-4 h-4 mr-1.5" /> Schedule appointment
          </Button>
        )}

        {entityType === "candidate" && (
          <div className="mt-4 flex items-center gap-2">
            {local.claimed_by ? (
              <>
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <span className="w-2 h-2 rounded-full" style={{ background: local.claimed_by_color }} />
                  Claimed by <span className="font-medium">{local.claimed_by_name}</span>
                </div>
                {(user?.role === "admin" || local.claimed_by === user?.id) && (
                  <Button size="sm" variant="ghost" onClick={release} data-testid="detail-release"
                    className="ml-auto text-slate-300 hover:text-white">
                    <Unlock className="w-3.5 h-3.5 mr-1" /> Release
                  </Button>
                )}
              </>
            ) : (
              <Button size="sm" onClick={claim} data-testid="detail-claim"
                className="bg-blue-600 hover:bg-blue-500 text-white">Claim Candidate</Button>
            )}
          </div>
        )}

        {/* Details grid */}
        <div className="mt-6">
          {entityType === "client" && !editing && (
            <div className="flex items-center justify-end mb-2">
              <Button size="sm" variant="outline" onClick={startEdit}
                className="h-7 text-xs border-slate-700 hover:border-blue-500"
                data-testid="detail-edit-btn">
                <Pencil className="w-3 h-3 mr-1" /> Edit details
              </Button>
            </div>
          )}
          {entityType === "client" && editing ? (
            <EditClientForm form={form} setForm={setForm} staffList={staffList} canAssign={isAdmin} />
          ) : (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {entityType === "candidate" && (<>
                <Field label="Phone" value={phone} />
                <Field label="Email" value={email} />
                <Field label="Position" value={local.position} />
                <Field label="City" value={local.city} />
                <Field label="Country" value={local.country} />
                <Field label="Permit" value={local.permit || "—"} />
                <Field label="Added" value={local.date_added ? formatDateTime(local.date_added) : "—"} />
              </>)}
              {entityType === "lead" && (<>
                <Field label="Phone" value={phone} />
                <Field label="Company" value={local.company} />
                <Field label="Source" value={local.lead_source} />
                <Field label="Type" value={local.lead_type} />
                <Field label="Priority" value={local.priority} />
                <Field label="Next follow-up" value={local.next_follow_up ? formatDateTime(local.next_follow_up) : "—"} />
                <Field label="Assigned to" value={local.assigned_to_name || "—"} />
              </>)}
              {entityType === "client" && (<>
                <Field label="Industry" value={local.industry} />
                <Field label="State" value={local.state} />
                <Field label="City" value={local.city} />
                <Field label="Phone" value={phone} />
                <Field label="Email" value={email} />
                <Field label="Website" value={local.website} />
                <Field label="Contact person" value={local.contact_person} />
                <Field label="Status" value={local.status} />
                <Field label="Assigned to" value={local.assigned_to_name || "—"} />
              </>)}
            </div>
          )}
          {entityType === "client" && editing && (
            <div className="flex items-center justify-end gap-2 mt-3">
              <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={saving}
                data-testid="detail-edit-cancel">Cancel</Button>
              <Button size="sm" onClick={saveEdit} disabled={saving}
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="detail-edit-save">
                <Check className="w-3.5 h-3.5 mr-1" /> Save changes
              </Button>
            </div>
          )}
          {entityType === "client" && local.last_edited_at && !editing && (
            <div className="mt-2 text-[11px] text-slate-500 text-right num"
              data-testid="detail-last-edited">
              Last edited by <span className="text-slate-300">{local.last_edited_by_name || "—"}</span> · {formatDateTime(local.last_edited_at)}
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-slate-200">Notes</div>
            <div className="text-xs text-slate-500 num">{notes.length}</div>
          </div>
          <Textarea
            placeholder="Add a note… (English, Bahasa Malaysia, or Mandarin)"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            className="bg-[#0a0e1a] border-slate-800 min-h-[70px]"
            data-testid="detail-note-input"
          />
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={addNote} disabled={!newNote.trim() || loading}
              data-testid="detail-note-submit"
              className="bg-blue-600 hover:bg-blue-500 text-white">Add note</Button>
          </div>
          <div className="mt-3 space-y-2 max-h-[320px] overflow-y-auto scrollbar-thin pr-1">
            {notes.map((n) => (
              <div key={n.id} className="rounded-md border hairline surface-2 p-3">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="w-2 h-2 rounded-full" style={{ background: n.author_color }} />
                  <span className="font-medium text-slate-200">{n.author_name}</span>
                  <span className="num">· {formatDateTime(n.created_at)}</span>
                </div>
                <div className="text-sm mt-1 whitespace-pre-wrap text-slate-200">{n.content}</div>
              </div>
            ))}
            {notes.length === 0 && (
              <div className="text-xs text-slate-500 text-center py-6">No notes yet.</div>
            )}
          </div>
        </div>

        {/* Danger zone — admin-only delete for the whole entity */}
        {isAdmin && (
          <div className="mt-8 pt-4 border-t hairline">
            <div className="text-[10px] uppercase tracking-wider text-red-400/70 mb-2 num">Danger zone</div>
            <Button variant="outline"
              onClick={() => setDeleteOpen(true)}
              className="w-full h-9 border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200"
              data-testid="detail-delete-btn">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete this {entityType}
            </Button>
            <div className="text-[11px] text-slate-500 mt-1">
              Admin-only. This cannot be undone. All associated notes{entityType === "client" ? " and appointments" : ""} will also be removed.
            </div>
          </div>
        )}
      </SheetContent>
      {entityType === "client" && (
        <ScheduleAppointmentDialog
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          client={local}
          onDone={() => onChanged?.()}
        />
      )}

      <AlertDialog open={deleteOpen} onOpenChange={(v) => !deleting && setDeleteOpen(v)}>
        <AlertDialogContent className="bg-[#0f1626] border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{title}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This removes the {entityType} and its notes permanently. There's no undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={doDelete}
              className="bg-red-600 hover:bg-red-500"
              data-testid="detail-delete-confirm">
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-md border hairline surface-2 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 num">{label}</div>
      <div className="text-sm mt-0.5 text-slate-200 break-words">{value || "—"}</div>
    </div>
  );
}

function EditClientForm({ form, setForm, staffList, canAssign }) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const setE = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid grid-cols-2 gap-3 text-sm" data-testid="detail-edit-form">
      <div className="col-span-2">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Company name</Label>
        <Input value={form.company_name} onChange={setE("company_name")}
          className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="edit-company_name" />
      </div>
      <FieldInput label="Industry"        value={form.industry}        onChange={setE("industry")} testid="industry" />
      <FieldInput label="State"           value={form.state}           onChange={setE("state")} testid="state" />
      <FieldInput label="City"            value={form.city}            onChange={setE("city")} testid="city" />
      <FieldInput label="Phone"           value={form.phone}           onChange={setE("phone")} testid="phone" />
      <FieldInput label="Email"           value={form.email}           onChange={setE("email")} testid="email" type="email" />
      <FieldInput label="Website"         value={form.website}         onChange={setE("website")} testid="website" />
      <FieldInput label="Contact person"  value={form.contact_person}  onChange={setE("contact_person")} testid="contact_person" />
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Status</Label>
        <Select value={form.status} onValueChange={set("status")}>
          <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="edit-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CLIENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {canAssign ? (
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Assigned to</Label>
          <Select value={form.assigned_to || "__unassigned"}
            onValueChange={(v) => setForm({ ...form, assigned_to: v === "__unassigned" ? "" : v })}>
            <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="edit-assigned_to">
              <SelectValue placeholder="Pick a staff" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__unassigned">— Unassigned</SelectItem>
              {staffList.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Assigned to</Label>
          <div className="mt-1 h-9 flex items-center text-xs text-slate-400 px-2 rounded-md bg-[#0a0e1a] border hairline">
            {form.assigned_to ? "Managed by admin" : "— Unassigned"}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldInput({ label, value, onChange, testid, type }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">{label}</Label>
      <Input value={value || ""} onChange={onChange} type={type || "text"}
        className="bg-[#0a0e1a] border-slate-800 mt-1"
        data-testid={`edit-${testid}`} />
    </div>
  );
}
